import { useRef, useState } from "react";
import { MessageSquareText, Quote } from "lucide-react";
import { CollapsibleSection } from "@/components/app/CollapsibleSection";
import { cn } from "@/lib/utils";

function QuestionBlock({ q, i, iv, highlight }) {
  const s = iv.scores[q.id];
  const turns = iv.turns.filter((t) => t.question_id === q.id);
  const hl = (text) => {
    if (!highlight || !s?.evidence || !text?.includes(s.evidence)) return text;
    const [a, ...rest] = text.split(s.evidence);
    return <>{a}<mark className="rounded bg-[#F1E2C0] px-0.5 text-ink" data-testid={`iv-evidence-highlight-${i}`}>{s.evidence}</mark>{rest.join(s.evidence)}</>;
  };
  return (
    <div id={`iv-q-${i}`} className={cn("scroll-mt-[340px] space-y-2 border-b border-line pb-4 last:border-0 transition-colors duration-500", highlight && "rounded-[6px] bg-[#F6EEDC] p-3")} data-testid={`iv-transcript-${i}`}>
      <div className="flex items-start justify-between gap-3"><p className="text-sm font-medium">{i + 1}. {q.text}</p>{s && <span className="shrink-0 rounded-full bg-brand-tint px-2 py-0.5 text-xs font-semibold text-brand">{s.score}/5</span>}</div>
      {turns.map((t, k) => <div key={k} className="space-y-1">{t.followup > 0 && <p className="text-xs italic text-ink-muted">Follow-up: {t.prompt}</p>}<p className="whitespace-pre-wrap rounded-lg bg-subtle p-3 text-sm">{t.answer ? hl(t.answer) : <em className="text-ink-muted">No answer</em>}</p></div>)}
      {s && <div className="space-y-1 text-xs text-ink-secondary" data-testid={`iv-rationale-${i}`}><p>{s.rationale}</p>{s.evidence_verified ? <p className="italic" data-testid={`iv-evidence-${i}`}>"{s.evidence}" <span className="not-italic font-medium text-brand">· Verified quote</span></p> : <p className="text-ink-muted" data-testid={`iv-evidence-missing-${i}`}>No verifiable quote</p>}</div>}
    </div>
  );
}

export function Transcript({ iv }) {
  const [open, setOpen] = useState(false);
  const [hl, setHl] = useState(null);
  const timer = useRef(null);
  const jump = (i) => {
    setOpen(true);
    setHl(i);
    setTimeout(() => document.getElementById(`iv-q-${i}`)?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" }), 280);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHl(null), 4000);
  };
  return (
    <div className="space-y-4">
      <section className="panel overflow-hidden" data-testid="iv-question-summary">
        <div className="flex items-center gap-2 border-b border-line px-5 py-3"><MessageSquareText size={16} className="text-brand" aria-hidden="true" /><h3 className="text-base font-semibold">Answers at a glance</h3></div>
        <ul className="divide-y divide-line">
          {iv.plan.map((q, i) => { const s = iv.scores[q.id]; return (
            <li key={q.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm" data-testid={`iv-summary-row-${i}`}>
              <span className="w-5 text-xs tabular-nums text-ink-muted">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate" title={q.text}>{q.text}</span>
              <span className="rounded-full bg-subtle px-2 py-0.5 text-xs text-ink-secondary">{q.competency}</span>
              <span className="w-10 text-right font-display font-semibold tabular-nums">{s ? `${s.score}/5` : "…"}</span>
              <button type="button" onClick={() => jump(i)} disabled={!s} className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline disabled:text-ink-muted disabled:no-underline" data-testid={`iv-jump-${i}`}>
                <Quote size={12} aria-hidden="true" /> Jump to evidence
              </button>
            </li>
          ); })}
        </ul>
      </section>
      <CollapsibleSection title="Full transcript" subtitle={`${iv.plan.length} questions · collapsed by default`} open={open} onOpenChange={setOpen} testId="iv-transcript">
        <div className="space-y-4">{iv.plan.map((q, i) => <QuestionBlock key={q.id} q={q} i={i} iv={iv} highlight={hl === i} />)}</div>
      </CollapsibleSection>
    </div>
  );
}
